import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertColor } from "@mui/material/Alert";
import { Box, Typography, Avatar, IconButton, Button, Menu, MenuItem, ListItemIcon, ListItemText, Divider, CircularProgress, Tooltip, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GroupIcon from "@mui/icons-material/Group";
import EditIcon from "@mui/icons-material/Edit";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import SmartToyIcon from "@mui/icons-material/SmartToy";

import InputBox from "./InputBox";
import MessageComponent from "./MessageComponent.tsx";
import UserActionsV2 from "./UserActionsV2.tsx";
import ConfirmDialog from "./ConfirmDialog.tsx";
import ListMembersDialog from "./ListMembersDialog.tsx";
import UserListV2 from "./UserListV2.tsx";
import InfoIcon from '@mui/icons-material/Info';
import { getSenderColor } from "../../utils/senderColor.ts";
import { IMessage, ContentBlock } from "../../models/Message.ts";
import InfiniteScroll from "react-infinite-scroll-component";
import store from '../../utils/store.ts';
import '../../styles/ChatPanel.css'
import EditDialog from "./EditDialog.tsx";
import { leaveChat, readChat, renameChat, sendMessage, uploadFile } from "../../resources/api.resource.ts";
import { UserMapValue } from "../../models/User.ts";
import TypingIndicator from "./TypingIndicator.tsx";
import HumanTypingIndicator from "./HumanTypingIndicator.tsx";
import { useTypingPresence } from "../../hooks/useTypingPresence";
import { usePageTitle } from "../../hooks/usePageTitle";
import { useConversationMessages, addMessageToCache } from "../../hooks/useConversationMessages";
import { useCollaborationAttachments, invalidateAttachmentsCache } from "../../hooks/useCollaborationAttachments";

interface ChatPanelProps {
  groupId?: string;
  uId: string;
  tenantId: string;
  chatType?: "direct" | "group" | "channel" | "project";
  chatTitle: string;
  instanceTitle?: string;
  allUsersMap?: Record<string, UserMapValue>;
  currentUser?: { id: string; name?: string };
  socket?: WebSocket;
  onLeaveRoomSuccess?: () => void;
  onSidebarRefresh?: () => void;
  onMarkAsRead?: (instanceId: string) => void;
  selectedItem: any;
  isLoadingCollaborations?: boolean;
  collaborationError?: string | null;
  subscribeToGroup?: (entry: { groupId: string; summaryId: string }) => void;
  initialDraft?: { html: string; files: File[] };
  onDraftChange?: (draft: { html: string; files: File[] }) => void;
}


// Persists thinking state per conversation so switching away and back preserves the indicator
const thinkingByConversation = new Map<string, boolean>();

const ChatPanel: React.FC<ChatPanelProps> = ({
  groupId,
  chatType = "direct",
  uId,
  tenantId,
  chatTitle,
  instanceTitle,
  allUsersMap = {},
  currentUser,
  socket,
  onLeaveRoomSuccess,
  onSidebarRefresh,
  onMarkAsRead,
  selectedItem,
  isLoadingCollaborations,
  collaborationError,
  subscribeToGroup,
  initialDraft,
  onDraftChange,
}) => {
  const theme = useTheme();
  const surface = (theme.palette as any).surface ?? {};
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Shared positioning for the custom undo/added toasts. On phones, pin to both
  // edges with a viewport-relative max width so the toast never overflows.
  const toastBoxStyle: React.CSSProperties = {
    position: 'absolute', bottom: 80, left: isMobile ? 12 : 24, zIndex: 1400,
    display: 'flex', alignItems: 'center', gap: '8px',
    backgroundColor: surface.toastBg ?? '#1A1F2E', borderRadius: '8px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.24)',
    padding: '10px 8px 10px 16px',
    minWidth: isMobile ? 0 : '300px',
    maxWidth: isMobile ? 'calc(100vw - 24px)' : '480px',
    ...(isMobile ? { right: 12 } : {}),
  };

  // React Query setup
  const queryClient = useQueryClient();

  // Use the custom hook for message fetching and caching
  const {
    messages,
    isLoading,
    isError,
    error,
    refetch: refetchMessages,
    hasMore,
    fetchNextPage,
    isFetchingNextPage,
  } = useConversationMessages({
    collaborationId: groupId,
    enabled: !!groupId,
  });

  const [files, setFiles] = useState<File[]>(initialDraft?.files ?? []);
  const [sending, setSending] = useState(false); // Loading state for sending messages
  const [notif, setNotif] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: "",
    severity: "info",
  });
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [removedNotif, setRemovedNotif] = useState<{ memberName: string; undo: () => Promise<void> } | null>(null);
  const [addedNotif, setAddedNotif] = useState<string | null>(null);

  useEffect(() => {
    if (!removedNotif) return;
    const t = setTimeout(() => setRemovedNotif(null), 5000);
    return () => clearTimeout(t);
  }, [removedNotif]);

  useEffect(() => {
    if (!addedNotif) return;
    const t = setTimeout(() => setAddedNotif(null), 4000);
    return () => clearTimeout(t);
  }, [addedNotif]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [listMembersOpen, setListMembersOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [title, setTitle] = useState(chatTitle);
  // Named here rather than in ChatScreen because this is the only component
  // that knows the conversation name, and "Marketing Team - IVA" tells a
  // screen-reader or window-switching user far more than "Chat" would (2.4.2).
  usePageTitle(title);
  const [drawerType, setDrawerType] = useState<string>("users");
  const [leaveMsg, setLeaveMsg] = useState("");
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [waitingForAgentResponse, setWaitingForAgentResponse] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const previousGroupIdRef = useRef<string | undefined>(groupId);
  const menuOpen = Boolean(menuAnchorEl);

  const selfEntry = allUsersMap[uId?.toLowerCase() ?? ''];
  const { typingUsers, notifyDraftActivity } = useTypingPresence({
    socket,
    tenantId,
    groupId,
    summaryId: selectedItem?.collaborationIdSummary ?? groupId,
    uId,
    selfDisplayName: currentUser?.name ?? selfEntry?.name ?? 'Someone',
    selfAvatarUrl: selfEntry?.avatarUrl ?? '',
  });

  const handleDraftChangeWithTyping = useCallback(
    (draft: { html: string; files: File[] }) => {
      onDraftChange?.(draft);

      notifyDraftActivity(draft.html.trim().length > 0);
    },
    [onDraftChange, notifyDraftActivity],
  );

  const { attachments, isLoading: attachmentsLoading } = useCollaborationAttachments({
    collaborationId: groupId,
    enabled: drawerOpen && drawerType === 'files',
  });
  const { getRootProps, getInputProps } = useDropzone({
    // Drag-and-drop only: InputBox owns the attach button / file picker, and the
    // dropzone root wraps the whole panel — so clicking it must NOT open the dialog.
    noClick: true,
    noKeyboard: true,
    onDrop: useCallback((droppedFiles: File[]) => {
      setFiles((prev) => [...prev, ...droppedFiles]);
    }, [])
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const prevMessageRef = useRef<HTMLDivElement>(null);
  const prevGroupIdRef = useRef<string | undefined>(undefined);
  const prevScrollHeightRef = useRef(0);

  const scrollToBottom = () => {
    setTimeout(() => {
      const el = document.getElementById('scrollableDiv');
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
  };


  const scrollNewMessageIntoView = () => {
    setTimeout(() => {
      const el = document.getElementById('scrollableDiv');
      if (!el) return;
      const lastMsg = lastMessageRef.current;
      if (lastMsg && lastMsg.offsetHeight > el.clientHeight) {
        const anchor = prevMessageRef.current ?? lastMsg;
        el.scrollTop += anchor.getBoundingClientRect().top - el.getBoundingClientRect().top;
      } else {
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  };

  // Fire fetchNextPage when user scrolls within 150px of the top
  const handleScrollForPagination = useCallback(() => {
    const el = document.getElementById('scrollableDiv');
    if (!el || !hasMore || isFetchingNextPage) return;
    if (el.scrollTop <= 150) {
      prevScrollHeightRef.current = el.scrollHeight;
      fetchNextPage();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isFetchingNextPage, fetchNextPage]);

  // Add error handling for React Query errors
  useEffect(() => {
    if (isError && error) {
      setNotif({
        open: true,
        message: `Failed to load messages: ${error.message}`,
        severity: "error",
      });
    }
  }, [isError, error]);

  // Scroll to bottom when a new conversation loads its first page of messages.
  // Does not fire during pagination (isFetchingNextPage) so older-page loads
  // don't yank the user back to the bottom.
  useEffect(() => {
    if (messages.length > 0 && !isFetchingNextPage && groupId !== prevGroupIdRef.current) {
      prevGroupIdRef.current = groupId;
      scrollToBottom();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, groupId, isFetchingNextPage]);

  // After a pagination fetch completes, restore scroll position so the view
  // doesn't jump to the top when older messages are prepended.
  useEffect(() => {
    if (prevScrollHeightRef.current > 0 && !isFetchingNextPage) {
      const el = document.getElementById('scrollableDiv');
      if (el) {
        el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
        prevScrollHeightRef.current = 0;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, isFetchingNextPage]);

  useEffect(() => {
    if(selectedItem === null) {
      setDrawerOpen(false);
      setTitle("");
      setListMembersOpen(false);
      setWaitingForAgentResponse(false);
    }
  }, [selectedItem]);

  // Track when groupId changes to show loading only if not cached
  useEffect(() => {
    if (groupId !== previousGroupIdRef.current) {
      if (groupId) {
        const cached = queryClient.getQueryData(['conversationMessages', groupId]);
        if (!cached) {
          setIsTransitioning(true);
        }
      }
      previousGroupIdRef.current = groupId;
    }
  }, [groupId]);

  // Stop transitioning when messages are loaded
  // Since each conversation has its own cache key (using groupId/instance ID),
  // we don't need to verify message ownership - just wait for loading to complete
  useEffect(() => {
    if (!isLoading && isTransitioning) {
      setIsTransitioning(false);
    }
  }, [isLoading, isTransitioning]);

  // Clear the thinking indicator when the assistant's reply lands in the message cache.
  // This handles the case where the response arrived while the user was viewing a different
  // conversation (the WS handler for this groupId was inactive at that time).
  useEffect(() => {
    if (!groupId || messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.sender?.role === 'assistant') {
      thinkingByConversation.delete(groupId);
      setWaitingForAgentResponse(false);
    }
  }, [messages, groupId]);

   useEffect(() => {
    if (!groupId) return;

    // Mark chat as read when opening a conversation
    const markAsRead = async () => {
      const summaryId = selectedItem?.collaborationIdSummary ?? selectedItem?.collaborationId ?? groupId;
      if (!summaryId) return;

      // groupId falls back to the summary id when there's no distinct collab_instance.
      // In that case omit instance_id so the backend resets all instances instead of
      // 404ing on a non-existent instance lookup.
      const instanceId = groupId && groupId !== summaryId ? groupId : undefined;

      try {
        await readChat({ collaboration_id: summaryId, instance_id: instanceId });
        if (groupId) {
          onMarkAsRead?.(groupId);
        }
      } catch (err) {
        console.error("Failed to mark chat as read", err);
      }
    };

    markAsRead();

    // Subscribe persistently — the group is never left when switching conversations.
    // All cache updates for this group are handled by usePersistentChatGroups in Workspaces.
    subscribeToGroup?.({
      groupId,
      summaryId: selectedItem?.collaborationIdSummary ?? groupId,
    });

    // Update UI state
    setTitle(chatTitle);
    setDrawerOpen(false);
    setWaitingForAgentResponse(thinkingByConversation.get(groupId) ?? false);

    // Scroll to bottom when messages load
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, socket]);


  // Remembers which control opened the drawer so focus can be handed back when
  // it closes, instead of being dropped to the top of the document.
  const drawerTriggerRef = useRef<HTMLElement | null>(null);

  const restoreDrawerFocus = () => {
    const trigger = drawerTriggerRef.current;
    drawerTriggerRef.current = null;
    trigger?.focus();
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    restoreDrawerFocus();
  };

  const toggleDrawer = (type: string) => {
    if (drawerOpen) {
      closeDrawer();
      return;
    }
    drawerTriggerRef.current = document.activeElement as HTMLElement | null;
    setDrawerType(type);
    setDrawerOpen(true);
  }
  useEffect(() => {
    if (!socket || !groupId) return;

    // Get the summary ID for comparison with WebSocket messages
    const summaryId = selectedItem?.collaborationIdSummary ?? groupId;

    const handleMessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "message" && msg.from === "group" && msg.dataType === "json") {
          const inner = JSON.parse(msg.data);
          // Only handle UI side effects for the currently active conversation.
          // Cache updates for all groups are handled by usePersistentChatGroups.
          if (inner.collaborationId === summaryId || inner.collaborationId === groupId) {
            if (inner.type === "system" && inner.message) {
              scrollToBottom();

              if (inner.event === "user_invited" || inner.event === "user_left" || inner.event === "user_removed") {
                onSidebarRefresh?.();

                if (inner.userId?.toLowerCase() === uId?.toLowerCase()) {
                  // User was removed from or left this group — evict cache and navigate away
                  queryClient.removeQueries({ queryKey: ['conversationMessages', groupId] });
                  setTitle("");
                  setDrawerType("users");
                  setDrawerOpen(false);
                  onLeaveRoomSuccess?.();
                  setNotif({
                    open: true,
                    message: inner.event === "user_removed"
                      ? "You have been removed from the group."
                      : "You left the group.",
                    severity: "warning",
                  });
                }
              }
            } else if (inner.status === 'streaming' && inner.session_id) {
              thinkingByConversation.delete(groupId);
              setWaitingForAgentResponse(false);
              scrollToBottom();
            } else if (inner.sender) {
              if (inner.sender.role === 'assistant') {
                thinkingByConversation.delete(groupId);
                setWaitingForAgentResponse(false);
              }
              // For an incoming message, show the beginning of tall messages
              // instead of jumping to the bottom and cutting off the start.
              scrollNewMessageIntoView();
            } else if (inner.type === "assistant_thinking") {
              thinkingByConversation.set(groupId, true);
              setWaitingForAgentResponse(true);
              }
        }
        }
      } catch (err) {
        console.error("WebSocket parse error", err);
      }
    };

    socket.addEventListener("message", handleMessage);

    return () => {
      socket.removeEventListener("message", handleMessage);
      // Note: leaveGroup is handled in the joinGroup effect's cleanup
      // to avoid leaving the group when only selectedItem changes
    };
  }, [socket, groupId, queryClient, uId, onSidebarRefresh, onLeaveRoomSuccess, selectedItem?.collaborationIdSummary]);

  const handleSendMessage = async (text?: string, uploadedFiles?: File[], contentBlocks?: ContentBlock[]) => {
    if (!groupId || (!text?.trim() && (!uploadedFiles || uploadedFiles.length === 0))) return;
    setSending(true);
    try {
      // Upload files first if there are any
      const uploadedAttachments = [];
      let uploadFailed = false;
      let failedFileName = "";
      let failedFileReason = "";

      if (uploadedFiles && uploadedFiles.length > 0) {
        for (const file of uploadedFiles) {
          try {
            const resp = await uploadFile(file, groupId);
            if (resp) {
              // API returns: { name, type, url }
              uploadedAttachments.push(resp);
            } else {
              // Upload returned null/undefined - consider it a failure
              uploadFailed = true;
              failedFileName = file.name;
              break;
            }
          } catch (err: any) {
            uploadFailed = true;
            failedFileName = file.name;
            failedFileReason = err.response?.data?.detail?.message || '';
            break;
          }
        }
      }

      // If any file upload failed, abort sending the message
      if (uploadFailed) {
        setNotif({
          open: true,
          message: `Failed to upload ${failedFileName}. ${failedFileReason} Message not sent.`,
          severity: "error"
        });
        throw new Error(`File upload failed: ${failedFileName}`);
      }

      // Build contentBlocks array with attachments
      let finalContentBlocks = contentBlocks || [];

      // If no contentBlocks provided (legacy), create simple text block
      if (!contentBlocks || contentBlocks.length === 0) {
        if (text?.trim()) {
          finalContentBlocks = [{ type: "text", content: text.trim() }];
        }
      }

      // Add file blocks to contentBlocks
      if (uploadedAttachments.length > 0) {
        uploadedAttachments.forEach((attachment: any) => {
          finalContentBlocks.push({
            type: "file",
            fileName: attachment.name,
            url: attachment.url,
            fileType: attachment.type
          });
        });
      }

      // Build clientMetadata
      const clientMetadata = {
        userAgent: navigator.userAgent,
        clientVersion: "2.1.0",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        deviceId: localStorage.getItem('deviceId') || 'web-client',
        ipAddress: "N/A"
      };

      // Build the new message payload matching API schema
      const payload = {
        collaboration_id: groupId,
        content: text?.trim() || "",
        attachments: uploadedAttachments.map((att: any) => ({
          type: att.type,
          url: att.url,
          name: att.name
        })),
        memberUids: [],
        contentBlocks: finalContentBlocks,
        plainTextContent: text?.trim() || "",
        replyToId: null,
        status: "active",
        clientMetadata: clientMetadata
      };

      // Create optimistic message for immediate UI feedback
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      const optimisticMessage: IMessage = {
        id: tempId,
        pk: tempId,
        collaborationId: groupId,
        tenantId: tenantId,
        createdDateTime: new Date().toISOString(),
        lastEditedDateTime: null,
        sender: {
          id: uId,
          role: 'human',
          displayName: currentUser?.name || 'You',
          avatarUrl: ''
        },
        contentBlocks: finalContentBlocks,
        plainTextContent: text?.trim() || "",
        replyToId: null,
        status: 'active',
        interactions: {
          reactions: {},
          aiQuality: []
        },
        clientMetadata: clientMetadata,
        editHistory: [],
        isPending: true // Mark as pending for blur effect
      };

      // Add optimistic message to cache immediately
      addMessageToCache(queryClient, groupId, optimisticMessage);
      scrollToBottom();

      // Send message to server
      const res = await sendMessage(payload);
      if (!res) throw new Error("Message send failed");

      // Invalidate attachments cache if files were sent
      if (uploadedAttachments.length > 0 && groupId) {
        invalidateAttachmentsCache(queryClient, groupId);
      }

      // Typing indicator is now driven by the assistant_thinking pubsub message
    } catch (err) {
      console.error("Send failed", err);
      // Check if this is a file upload error (we already showed specific error message)
      const isFileUploadError = err instanceof Error && err.message.startsWith('File upload failed:');

      if (!isFileUploadError) {
        // Only show generic error message for non-file-upload errors
        setNotif({ open: true, message: "Failed to send message", severity: "error" });
      }

      // Rethrow the error so InputBox knows the send failed and keeps the message
      throw err;
    } finally {
      setSending(false);
    }
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuAction = (action: "Manage Members" | "Rename" | "Leave") => {
    if (action === "Manage Members") {
      setListMembersOpen(true);
    } else if (action === "Rename") {
      setShowEditDialog(true);
    } else if (action === "Leave") {
      setConfirmLeave(true);
    }
    handleMenuClose();
  };

  const handleLeaveRoom = async () => {
    if (!groupId) return;
    try {
      // Leaving is a collaboration-level action, so send the summary
      // collaboration id (the pk), not the collab_instance id. Passing the
      // instance id makes the backend miss the collaboration doc and fall back
      // to treating it as a DM ("Cannot leave a direct message conversation.").
      const leaveId = selectedItem?.collaborationIdSummary || groupId;
      const res = await leaveChat({ collaboration_id: leaveId });
      if (!res) throw new Error("Leave group failed");
      setNotif({ open: true, message: "You left the room", severity: "warning" });
      // setMessages([])
      // onLeaveRoomSuccess?.();
      setConfirmLeave(false)
      // setMessages([]);
      // setTitle("");
      // setDrawerOpen(false)
      onLeaveRoomSuccess?.();
    } catch (err) {
      console.error(err);
      setNotif({ open: true, message: "Failed to leave", severity: "error" });
    }
  };

  const handleRename = async (name: string) => {
    try {
      const res = await renameChat({ collaboration_id: selectedItem?.collaborationIdSummary ?? groupId, name: name });
      if (!res) throw new Error("Rename failed");
      setNotif({ open: true, message: "Renamed successfully.", severity: "success" });
      setShowEditDialog(false);
      setTitle(name);
      onSidebarRefresh?.();
    } catch (err) {
      console.error(err);
      setNotif({ open: true, message: "Failed to Rename", severity: "error" });
    }
  };

  // Find collaboration by collaborationId or by instance id
  const findCollaboration = (id: string) => {
    return store.getState().collaborations.find((collab) =>
      collab.collaborationId === id ||
      collab.collab_instances?.some((inst: any) => inst.id === id)
    );
  };

  const getGroupName = (): string => {
    return findCollaboration(groupId ?? '')?.name ?? "";
  }

  const getDrawerList = (id: string) => {
    switch(drawerType) {
      case  'users':
        let users: any[] = [];
        // Prefer the store's collaboration members, but fall back to the
        // selected item's memberUids — right after creating a group the store
        // hasn't been refreshed yet, so the store lookup returns nothing.
        const storeMembers = findCollaboration(id)?.memberUids;
        let members: string[] | undefined =
          storeMembers && storeMembers.length > 0 ? storeMembers : selectedItem?.memberUids;
        if(members && members.length > 0) {
          members.forEach((member: string) => {
            const key = member?.toLowerCase();
            if(allUsersMap?.[key]?.name) {
              let memberObj = {
                name: allUsersMap?.[key]?.name,
                id: member,
                email: allUsersMap?.[key]?.email,
                role: allUsersMap?.[key]?.userType === 'agent' ? 'assistant' as const : 'human' as const
              }
              users.push(memberObj);
            }

          });
          const currentUserId = store.getState().user?.userId?.toLowerCase();
          const membersLower = members.map((m) => m?.toLowerCase());
          const alreadyListed = currentUserId
            ? users.some((u) => u.id?.toLowerCase() === currentUserId)
            : false;
          if (store.getState().user && membersLower.includes(currentUserId) && !alreadyListed) {
             users.push({
              name: store.getState().user.displayName,
              id: store.getState().user.userId,
              role: 'human' as const
             });
          }
        }
        return users;
      case 'files':
        return attachments.map(att => ({
          id: att.id,
          name: att.name,
          url: att.url,
          fileType: att.fileType,
          uploadedBy: att.uploadedByName,
          uploadedAt: att.createdDateTime,
          senderId: att.uploadedBy,
        }));
      default:
        return [];
    }
  }

  const agentAvatar = useMemo(() => {
    const memberUids: string[] = selectedItem?.memberUids ?? [];
    const agentEntry = memberUids
      .map((uid: string) => allUsersMap[uid.toLowerCase()])
      .find((u) => u?.userType === 'agent');
    return agentEntry ? { src: agentEntry.avatarUrl || undefined, name: agentEntry.name } : null;
  }, [selectedItem?.memberUids, allUsersMap]);

  const headerAvatar = useMemo(() => {
    if (chatType === 'direct' && selectedItem?.memberUids) {
      const otherUid = (selectedItem.memberUids as string[]).find(
        (u) => u.toLowerCase() !== uId.toLowerCase()
      );
      const other = otherUid ? allUsersMap[otherUid.toLowerCase()] : null;
      if (other) {
        const color = getSenderColor(otherUid!);
        const isAgent = other.userType === 'agent';
        return {
          url: other.avatarUrl || undefined,
          sx: isAgent
            ? (other.avatarUrl
                ? {
                    bgcolor: '#fff',
                    border: '1px solid',
                    borderColor: 'divider',
                    '& .MuiAvatar-img': { objectFit: 'contain', p: '3px', boxSizing: 'border-box' },
                  }
                : { bgcolor: theme.palette.primary.main, color: '#fff' })
            : { bgcolor: color.avatarBg, color: color.avatarFg },
          child: isAgent
            ? <SmartToyIcon fontSize="small" />
            : (other.name?.charAt(0).toUpperCase() ?? '?'),
        };
      }
    }
    const color = getSenderColor(groupId ?? title);
    return {
      url: undefined,
      sx: { bgcolor: color.avatarBg, color: color.avatarFg },
      child: title?.charAt(0).toUpperCase() ?? '?',
    };
  }, [chatType, selectedItem?.memberUids, allUsersMap, uId, groupId, title, theme.palette.primary.main]);

  const renderEmptyState = () => {
    if (collaborationError) {
      return (
        <Box sx={{
          padding: "40px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%"
        }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {collaborationError}
          </Typography>
        </Box>
      );
    }

    return (
      <div className="chatEmptyState">
        <svg width="84" height="84" viewBox="0 0 84 84" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10.5 70.001L15.05 56.351C6.916 44.3215 10.059 28.799 22.4 20.042C34.741 11.2885 52.465 12.006 63.8575 21.722C75.25 31.4415 76.79 47.153 67.459 58.4755C58.128 69.798 40.8065 73.228 26.95 66.501L10.5 70.001Z" stroke="#8E97A3" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M33.9961 36.1279H34.0281M50.0226 36.1279H50.0546M33.9961 48.9491C35.0406 50.0152 36.2874 50.8621 37.6634 51.4403C39.0393 52.0185 40.5168 52.3163 42.0093 52.3163C43.5018 52.3163 44.9793 52.0185 46.3553 51.4403C47.7313 50.8621 48.978 50.0152 50.0226 48.9491" stroke="#8E97A3" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <p className="chatEmptyState__title">No Conversation History</p>
        <p className="chatEmptyState__subtitle">Your conversation history will appear here</p>
      </div>
    );
  };

  const renderMessages = () => {
    if (!messages.length) return renderEmptyState();

    return messages.map((msg, idx) => {
      return (
        <div
          key={msg.id || idx}
          ref={
            idx === messages.length - 1
              ? lastMessageRef
              : idx === messages.length - 2
                ? prevMessageRef
                : undefined
          }
          style={{ marginBottom: "6px" }}
        >
          <MessageComponent
            key={msg.id || idx}
            message={msg}
            uId={uId}
            chatType={msg.sender.role}
            collaborationId={groupId ?? ""}
            collaborationIdSummary={selectedItem?.collaborationIdSummary}
            tenantId={tenantId}
            allUsersMap={allUsersMap}
            socket={msg.status === 'streaming' ? socket : undefined}
          />
        </div>
      );
    });
  };

  // Show loading overlay when switching to an uncached conversation, or while a new chat is being created
  if ((isTransitioning || isLoading) && groupId || isLoadingCollaborations) {
    const skeletonColor = surface.divider ?? 'rgba(128,128,128,0.15)';
    const skeletonPulse: React.CSSProperties = { backgroundColor: skeletonColor, animation: 'skeletonPulse 1.5s ease-in-out infinite' };
    return (
      <Stack spacing={0} sx={{ height: "100%", position: 'relative' }}>
        <div className="chatHeader">
          <div className="chatHeader__title">
            <div style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, backgroundColor: skeletonColor }} />
            <h2 className="chatHeader__name">{title}</h2>
          </div>
          <div className="chatHeader__icons" />
        </div>
        <style>{`
          @keyframes skeletonPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
        <div role="status" aria-label="Loading conversation" style={{ flex: 1, backgroundColor: surface.chatPanelBg ?? '#fff', padding: isMobile ? '20px' : '40px', display: 'flex', flexDirection: 'column', gap: '40px', overflow: 'hidden' }}>
          {/* Header skeleton */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
              <div style={{ width: 49, height: 49, borderRadius: '50%', flexShrink: 0, ...skeletonPulse }} />
              <div style={{ width: 265, maxWidth: '100%', height: 32, borderRadius: 16, ...skeletonPulse }} />
              <div style={{ width: 24, height: 24, borderRadius: 16, flexShrink: 0, ...skeletonPulse }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: 24, height: 24, borderRadius: 16, flexShrink: 0, ...skeletonPulse }} />
              <div style={{ width: 24, height: 24, borderRadius: 16, flexShrink: 0, ...skeletonPulse }} />
              <div style={{ width: 24, height: 24, borderRadius: 16, flexShrink: 0, ...skeletonPulse }} />
            </div>
          </div>
          {/* Center empty-state skeleton */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '28px' }}>
            <div style={{ width: 96, height: 96, borderRadius: '50%', border: '8px solid #eceef0', backgroundColor: 'transparent', flexShrink: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', maxWidth: '100%' }}>
              <div style={{ width: 274, maxWidth: '100%', height: 24, borderRadius: 16, ...skeletonPulse }} />
              <div style={{ width: 348, maxWidth: '100%', height: 16, borderRadius: 16, ...skeletonPulse }} />
            </div>
          </div>
          {/* Input bar skeleton */}
          <div style={{ width: '100%', height: 78, borderRadius: 16, flexShrink: 0, ...skeletonPulse }} />
        </div>
      </Stack>
    );
  }

  return (
    <Stack spacing={0} sx={{ height: "100%", flexDirection: 'row', position: 'relative' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, position: 'relative', bgcolor: 'surface.chatPanelBg' }}>
      <div className="chatHeader">
        <div className="chatHeader__title">
          {/* Decorative: the conversation name follows immediately in the h2
              below, so a described avatar would just repeat it. */}
          <Avatar
            src={headerAvatar.url}
            alt=""
            sx={{ width: 40, height: 40, flexShrink: 0, ...headerAvatar.sx }}
          >
            {headerAvatar.child}
          </Avatar>
          {/* The conversation name is the heading for the transcript below it,
              so it is a real h2 under ChatScreen's h1 rather than a styled span.
              chatHeader.css supplies the size/weight; the reset below stops the
              UA h2 defaults from changing the visual result. */}
          <h2 className="chatHeader__name">{title}</h2>
          {instanceTitle && (
            <span className="chatHeader__instance">/ {instanceTitle}</span>
          )}
          <Tooltip title="Rename">
            <IconButton
              size="small"
              aria-label="Rename conversation"
              onClick={() => setShowEditDialog(true)}
              className="headerMenuButton"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <div className="chatHeader__icons">
          <UserActionsV2
            onOpenDrawer={toggleDrawer}
            type={chatType}
            groupId={groupId ?? ""}
          />
        </div>
      </div>

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleMenuAction("Manage Members")}>
          <ListItemIcon>
            <GroupIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Manage Members" />
        </MenuItem>

        {(chatType === 'group' || chatType === 'project') && (
          <MenuItem onClick={() => handleMenuAction("Rename")}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Rename" />
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={() => handleMenuAction("Leave")}>
          <ListItemIcon>
            <ExitToAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Leave Room" />
        </MenuItem>
      </Menu>
      <div className="chatWrapperWorkspaces" ref={scrollRef} style={{ display: 'flex', position: 'relative', flex: 1, overflow: 'hidden' }} {...getRootProps()}>
        {/* Drag-and-drop only (noClick/noKeyboard), but the input is still in the
            a11y tree, so it needs a name of its own. */}
        <input {...getInputProps({ 'aria-label': 'Attach files' })} />
        <div className="chatWrapper" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>

            <div
              id="scrollableDiv"
              role="log"
              aria-live="polite"
              // "text" made screen readers re-announce the assistant's reply on
              // every streamed token. Only whole-node additions are relevant.
              aria-relevant="additions"
              aria-atomic="false"
              // While a reply streams, the log is explicitly marked busy so
              // assistive tech holds its announcements and delivers the finished
              // message once, instead of narrating it token by token. The
              // TypingIndicator covers the interim.
              aria-busy={messages.some((m) => m.status === 'streaming') || undefined}
              aria-label="Conversation messages"
              style={{
                flex: 1,
                height: 0,
                overflow: "auto",
                overflowX: "hidden",
                width: "100%",
                display: 'flex',
                flexDirection: 'column',
              }}
              onScroll={handleScrollForPagination}
            >
              {isFetchingNextPage && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '8px' }} role="status" aria-label="Loading older messages">
                  <CircularProgress size={24} />
                </div>
              )}
              {messages.length === 0 ? renderEmptyState() : (
                <InfiniteScroll
                  dataLength={messages.length}
                  next={() => {}}
                  hasMore={false}
                  loader={null}
                  style={{ overflow: "hidden", padding: "10px", paddingBottom: "60px" }}
                  scrollableTarget="scrollableDiv"
                >
                  {renderMessages()}
                  {leaveMsg && (
                    <div className="leave-message">
                      <InfoIcon />
                      <div className="message-text">{leaveMsg || ""}</div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </InfiniteScroll>
              )}
            </div>

          <div className="footerContainer" style={{ position: 'relative' }}>
            {(typingUsers.length > 0 || waitingForAgentResponse) && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: '10px',
                marginBottom: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <HumanTypingIndicator users={typingUsers} />
                {waitingForAgentResponse && (
                  <TypingIndicator avatarSrc={agentAvatar?.src} avatarName={agentAvatar?.name} />
                )}
              </div>
            )}
            <div className="inputContainer">
              <InputBox
                groupId={groupId ?? ""}
                error={undefined}
                onSend={handleSendMessage}
                files={files}
                setFiles={setFiles}
                loading={sending}

                mentionableUsers={getDrawerList(groupId ?? '')}
                initialDraft={initialDraft}
                onDraftChange={handleDraftChangeWithTyping}
              />
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={notif.open}
        autoHideDuration={3000}
        onClose={() => setNotif({ ...notif, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          severity={notif.severity}
          variant="filled"
          onClose={() => setNotif({ ...notif, open: false })}
        >
          {notif.message}
        </MuiAlert>
      </Snackbar>

      {removedNotif && (
        <Box role="status" aria-live="polite" style={toastBoxStyle}>
          <Typography style={{ flex: 1, fontSize: '0.8125rem', color: surface.toastText ?? '#ffffff', fontWeight: 500 }}>
            Successfully removed &ldquo;{removedNotif.memberName}&rdquo; from &ldquo;{title}&rdquo; Group!
          </Typography>
          <Button
            size="small"
            onClick={async () => { await removedNotif.undo(); setRemovedNotif(null); }}
            style={{ fontSize: '0.8125rem', fontWeight: 600, textTransform: 'none', color: '#7EB3FF', flexShrink: 0 }}
          >
            Undo
          </Button>
          <IconButton size="small" aria-label="Dismiss notification" onClick={() => setRemovedNotif(null)} style={{ flexShrink: 0 }}>
            <CloseIcon style={{ fontSize: 16, color: surface.toastText ?? '#ffffff' }} />
          </IconButton>
        </Box>
      )}

      {addedNotif && (
        <Box role="status" aria-live="polite" style={toastBoxStyle}>
          <Typography style={{ flex: 1, fontSize: '0.8125rem', color: surface.toastText ?? '#ffffff', fontWeight: 500 }}>
            Successfully added &ldquo;{addedNotif}&rdquo; to Group &ldquo;{title}&rdquo;!
          </Typography>
          <IconButton size="small" aria-label="Dismiss notification" onClick={() => setAddedNotif(null)} style={{ flexShrink: 0 }}>
            <CloseIcon style={{ fontSize: 16, color: surface.toastText ?? '#ffffff' }} />
          </IconButton>
        </Box>
      )}

      <ConfirmDialog
        color="error"
        open={confirmLeave}
        title="Leave Room?"
        message="Are you sure you want to leave this room?"
        onConfirm={handleLeaveRoom}
        onClose={() => setConfirmLeave(false)}
      />

      <EditDialog
        open={showEditDialog}
        title="Rename"
        name={getGroupName()}
        onConfirm={handleRename}
        onClose={() => setShowEditDialog(false)}
      />

      <ListMembersDialog
        open={listMembersOpen}
        groupId={groupId ?? ""}
        currentUserId={uId}
        onClose={() => setListMembersOpen(false)}
        onRemoved={(userId) =>
          setNotif({ open: true, message: `${userId} removed from room`, severity: "info" })
        } 
        onLeave={() =>
          setNotif({ open: true, message: "You left the room", severity: "warning" })
        }
        onPinclose={()=>closeDrawer()}
      />
      </Box>

      {drawerOpen && (
        <UserListV2
          refreshSidebar={() => onSidebarRefresh?.()}
          refreshRightPanel={() => getDrawerList(groupId ?? '')}
          uId={uId}
          drawerType={drawerType}
          showAddRemove={chatType === 'group' || chatType === 'project'}
          list={getDrawerList(groupId ?? '')}
          listLoading={drawerType === 'files' && attachmentsLoading}
          onAction={(action: string) => {
            if (action === 'close') closeDrawer();
            if (action === 'leave') setConfirmLeave(true);
          }}
          onRemoved={(memberName, undoFn) => setRemovedNotif({ memberName, undo: undoFn })}
          onAdded={(memberName) => setAddedNotif(memberName)}
          groupId={groupId ?? ''}
          groupName={title}
          collaborationId={selectedItem?.collaborationIdSummary ?? groupId ?? ''}
        />
      )}
    </Stack>
  );
};

export default ChatPanel;
